import type { ComputedRef, InjectionKey, ShallowRef } from 'vue';

/** One mounted `CommandItem`, as the root sees it. */
export interface CommandItemEntry {
    /** The option's element id, used by the keyboard navigation and `aria-activedescendant` */
    id: string;
    /** What the search matches and what `select` gets: the `value` prop, else the item's text */
    value: ComputedRef<string>;
    keywords: ComputedRef<readonly string[]>;
    disabled: ComputedRef<boolean>;
    element: Readonly<ShallowRef<HTMLElement | null>>;
    /** The `CommandGroup` around the item */
    group: symbol | undefined;
}

export interface CommandContext {
    search: ComputedRef<string>;
    setSearch: (text: string) => void;
    /** True while the search has text */
    searching: ComputedRef<boolean>;
    listId: string;
    list: ShallowRef<HTMLElement | null>;
    activeId: ComputedRef<string | undefined>;
    hasVisibleItems: ComputedRef<boolean>;
    isVisible: (entry: CommandItemEntry) => boolean;
    isGroupVisible: (group: symbol) => boolean;
    highlight: (id: string) => void;
    onKeydown: (event: KeyboardEvent) => void;
    register: (entry: CommandItemEntry) => () => void;
}

export const commandKey: InjectionKey<CommandContext> = Symbol('Command');
export const commandGroupKey: InjectionKey<symbol> = Symbol('CommandGroup');
/** Called after an item is selected, by a `CommandDialog` around the Command */
export const commandSelectKey: InjectionKey<() => void> = Symbol('CommandSelect');

export const useCommandContext = (part: string): CommandContext => {
    const context = inject(commandKey, null);
    if (!context) throw new Error(`<${part}> must be used inside <Command>`);

    return context;
};
